// Public Infinity Stream client: publish winners, browse, favorite.

const PUBLISH_KEY = 'sogni-infinity-publish';

export function publishEnabled() {
  return localStorage.getItem(PUBLISH_KEY) === '1';
}

export function setPublishEnabled(on) {
  localStorage.setItem(PUBLISH_KEY, on ? '1' : '0');
}

// The byline is not sent: the server reads it off the Sogni session that the
// shared .sogni.ai cookie carries with this request, so a tile can only ever
// be credited to the account that actually published it. `replaces` names a
// stream entry this image should update in place (an upscale of a tile
// already on the wall); the server honours it only for the entry's author.
export async function publishTile({ url, subject, prompt, seed, replaces }) {
  const res = await fetch('/api/stream', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, subject, prompt, seed, ...(replaces ? { replaces } : {}) })
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `publish failed (${res.status})`);
  }
  return res.json();
}

// Keepers retain their pixels in IndexedDB because the original Sogni URL can
// expire. Publish that stored blob directly so a tile can be shared days after
// it was kept. Metadata rides in query parameters while the body stays binary.
export async function publishKeeperTile({ blob, subject, prompt, seed }) {
  const params = new URLSearchParams({
    subject: subject || '',
    prompt: prompt || '',
    ...(Number.isFinite(Number(seed)) ? { seed: String(seed) } : {})
  });
  const res = await fetch(`/api/stream/keeper?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: blob
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `publish failed (${res.status})`);
  }
  return res.json();
}

// One page of the wall. The server bounds `limit` and returns `total`, so the
// caller pages with `offset` and knows when to stop.
export async function fetchStream({ sort = 'new', limit = 60, offset = 0 } = {}) {
  const params = new URLSearchParams({ sort, limit: String(limit), offset: String(offset) });
  const res = await fetch(`/api/stream?${params.toString()}`);
  if (!res.ok) throw new Error(`stream unavailable (${res.status})`);
  return res.json();
}

// Who the server sees us as, and whether it will let us moderate. Only ever a
// hint for which controls to show — deletion is authorized again server-side.
export async function fetchWhoami() {
  const res = await fetch('/api/whoami', { cache: 'no-store' });
  if (!res.ok) return { username: null, canModerate: false };
  return res.json();
}

// Remove a tile from the public wall. The JSON content type is deliberate even
// with no body — see toggleStreamFavorite below for why it forces a preflight.
export async function deleteStreamEntry(id) {
  const res = await fetch(`/api/stream/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `delete failed (${res.status})`);
  }
  return res.json();
}

// Report a published tile to this app's own moderation queue. Enough
// independent reports withhold it from the wall pending review; the response
// says only that it was received, never the tally.
export async function flagStreamEntry(id, { reason, note }) {
  const res = await fetch(`/api/stream/${encodeURIComponent(id)}/flag`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, note })
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `report failed (${res.status})`);
  }
  return res.json();
}

// Moderator: dismiss the reports on a tile and put it back on the wall.
export async function unflagStreamEntry(id) {
  const res = await fetch(`/api/stream/${encodeURIComponent(id)}/unflag`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `could not clear reports (${res.status})`);
  }
  return res.json();
}

// The JSON content type is deliberate on a request with no body: it makes this
// a preflighted request, so a cross-origin attempt has to clear the CORS
// allow-list before the browser will even send it. The server's Origin check
// is the lock; this is the second one.
export async function toggleStreamFavorite(id) {
  const res = await fetch(`/api/stream/${encodeURIComponent(id)}/favorite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!res.ok) throw new Error(`could not favorite (${res.status})`);
  return res.json();
}
